import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Button primitive aligned to HARMONIZATION.md § 4.5 (advisory-mode size
 * matrix) + § 6.4 (per-state colour matrix) + § 14 (focus shadow).
 *
 * Intent matrix (§ 6.4)
 *
 *   default     primary CTA, brand blue
 *   secondary   secondary CTA, brand green
 *   ghost       transparent until hover; neutral ink palette
 *   outline     neutral surface with ink border
 *   destructive semantic.danger red
 *   link        text-only with underline
 *
 * Size matrix (§ 4.5, advisory mode)
 *
 *   sm        h-8  px-3 py-1.5     text-sm
 *   default   h-9  px-4 py-2       text-sm     (= md)
 *   md        h-9  px-4 py-2       text-sm     (= default — explicit alias)
 *   lg        h-11 px-6 py-3       text-base
 *   icon-sm   h-8  w-8
 *   icon      h-9  w-9                            (default icon)
 *   icon-lg   h-11 w-11                           (= mobile touch floor 44px)
 *
 * Focus state (§ 14) is the shared `shadow-focus-blue` token. Variant
 * colours come from the canonical `--color-blue-N` / `--color-ink-N`
 * / `--color-semantic-*` palette defined in `globals.css` — NOT the
 * shadcn HSL aliases (`bg-primary`, `text-primary-foreground`, etc.),
 * which the § 17 anti-pattern table forbids in chrome.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus-blue)] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-[var(--color-blue-500)] text-white shadow hover:bg-[var(--color-blue-600)]',
        destructive:
          'bg-[var(--color-semantic-danger)] text-white shadow-sm hover:brightness-95',
        outline:
          'border border-border bg-background text-foreground shadow-sm hover:bg-muted hover:text-foreground',
        secondary:
          'bg-[var(--color-semantic-success)] text-white shadow-sm hover:brightness-95',
        ghost:
          'text-foreground hover:bg-muted hover:text-foreground',
        link: 'text-[var(--color-blue-600)] underline-offset-4 hover:underline',
      },
      // Sub-44px sizes carry a `coarse:` (pointer: coarse) floor so the tap
      // target hits the 44×44pt mobile minimum on touch devices, while the
      // compact height is preserved for fine-pointer (mouse) desktop use
      // (#1471). This makes every `<Button>` callsite touch-compliant by
      // construction — no per-callsite `min-h-11` needed. `lg`/`icon-lg`
      // are already 44px so they need no floor.
      size: {
        sm: 'h-8 px-3 py-1.5 coarse:min-h-11 coarse:min-w-11',
        // `default` and `md` resolve to the same advisory-mode row; both
        // names are exposed so the size table can be addressed by either
        // shadcn-style (`default`) or handbook-style (`md`) keys.
        default: 'h-9 px-4 py-2 coarse:min-h-11 coarse:min-w-11',
        md: 'h-9 px-4 py-2 coarse:min-h-11 coarse:min-w-11',
        lg: 'h-11 px-6 py-3 text-base',
        'icon-sm': 'h-8 w-8 coarse:min-h-11 coarse:min-w-11',
        icon: 'h-9 w-9 coarse:min-h-11 coarse:min-w-11',
        // 44px square == iOS/Android mobile-touch floor. Used by chrome
        // bell/avatar triggers at < md breakpoint.
        'icon-lg': 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
